import React, { useEffect } from 'react';
import { Form, FormGroup, Input, Label, Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';
import Body from '../Components/layout/Body';
import { ISSUE_BUTTONS_OVERALL, ISSUE_BUTTONS_MOTORIC, ISSUE_BUTTONS_COGPERC, ISSUE_BUTTONS_TECH, ISSUE_BUTTONS_NOTECH } from './ConstIssueBtns';
import { AM_CATEGORIES } from './ConstAccessMethodsMock';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const TD_RATE_ISSUES_HELP = `
  <div>
    <p>
    Here you can get into more detail about what happened during the Test-drive.  
    The checkboxes represent issues you may have observed.  
    (Note that some of the checkboxes will change, depending on what access method you’re using. 
    For example, you won’t see checkboxes about mouse movement if you used a keyboard.)
    </p>
    <p>
    We hope the checkboxes will streamline the process of recording observations. 
    And they may be useful prompts for thinking about all the issues that can be involved during a Test-drive. 
    We also use your selected issues to provide you with some suggestions that might help improve things – you'll see these in the Interpret section coming up.
    </p> 
    <p>
    Finally, you can enter any other observations or comments in the Your Comments field.
    </p> 
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  isLowTechPath: PropTypes.bool,
  activeAccessMethodCategory: PropTypes.string,
  activeTestDriveIssuesNoted: PropTypes.arrayOf(PropTypes.string),
  activeTestDrivePractitionerComments: PropTypes.string,
  onChangeActiveTestDriveIssuesNoted: PropTypes.func.isRequired,
  onChangeActiveTestDrivePractitionerComments: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  isLowTechPath: false,
  activeAccessMethodCategory: '',
  activeTestDriveIssuesNoted: [],
  activeTestDrivePractitionerComments: ''
};

//content sub-component with unique content for this page
function TestDriveRateIssuesContent(props) {
  // const firstName = props.firstName;
  const isLowTechPath = props.isLowTechPath;
  const activeAccessMethodCategory = props.activeAccessMethodCategory;
  const activeTestDriveIssuesNoted = props.activeTestDriveIssuesNoted;
  const activeTestDrivePractitionerComments = props.activeTestDrivePractitionerComments;
  const onChangeActiveTestDriveIssuesNoted = props.onChangeActiveTestDriveIssuesNoted;
  const onChangeActiveTestDrivePractitionerComments = props.onChangeActiveTestDrivePractitionerComments;
  
  //figure out what checkboxes to display
  let overallSpecBtns = (activeAccessMethodCategory) ? ISSUE_BUTTONS_OVERALL[activeAccessMethodCategory] : [];
  let motorSpecBtns = (activeAccessMethodCategory) ? ISSUE_BUTTONS_MOTORIC[activeAccessMethodCategory] : [];
  let cogSpecBtns = (activeAccessMethodCategory) ? ISSUE_BUTTONS_COGPERC[activeAccessMethodCategory] : [];
  let techSpecBtns = (activeAccessMethodCategory) ? ISSUE_BUTTONS_TECH[activeAccessMethodCategory] : [];
  let noTechSpecBtns = (activeAccessMethodCategory) ? ISSUE_BUTTONS_NOTECH[activeAccessMethodCategory] : [];

  //include the 'any' buttons along with those for the specific type of access method
  const overallBtns = (overallSpecBtns) ? overallSpecBtns.concat(ISSUE_BUTTONS_OVERALL.any) : ISSUE_BUTTONS_OVERALL.any;
  const motorBtns = (motorSpecBtns) ? motorSpecBtns.concat(ISSUE_BUTTONS_MOTORIC.any) : ISSUE_BUTTONS_MOTORIC.any;
  const cogBtns = (cogSpecBtns) ? cogSpecBtns.concat(ISSUE_BUTTONS_COGPERC.any) : ISSUE_BUTTONS_COGPERC.any;
  const noTechBtns = (noTechSpecBtns && activeAccessMethodCategory) ? noTechSpecBtns.concat(ISSUE_BUTTONS_NOTECH.any) : ISSUE_BUTTONS_NOTECH.any;
  //don't include the general tech button if we have any more specific ones
  const techBtns = (techSpecBtns && activeAccessMethodCategory) ? techSpecBtns : ISSUE_BUTTONS_TECH.any;

  return(
    <>
    <p>Check any of the following difficulties that you observed during the Test-drive.</p>
    {(!activeAccessMethodCategory)
      ? <p><strong>Note: </strong>These are general issues that could apply to any access method, since we don't know what type of access method you're using.</p>
      : null
    }
    <Form>
      <Row>
        <Col sm="6">
          <FormGroup tag="fieldset">
          <legend>Overall, were there challenges with:</legend>
          {overallBtns.map((btn, index) =>
            <FormGroup check key={index}>
              <Input
                id={btn.value}
                value={btn.value}
                checked={activeTestDriveIssuesNoted.indexOf(btn.value) > -1}
                onChange={onChangeActiveTestDriveIssuesNoted}
                name="overall-issues"
                type="checkbox"
              />
              {' '}
              <Label for={btn.value} check>
                {btn.label}
                {' '}
              </Label>
            </FormGroup>
          )}
          </FormGroup>
          {(!isLowTechPath) ?
            <FormGroup tag="fieldset">
            <legend>Possible technical issues:</legend>
            {techBtns.map((btn, index) =>
              <FormGroup check key={index}>
                <Input
                  id={btn.value}
                  value={btn.value}
                  checked={activeTestDriveIssuesNoted.indexOf(btn.value) > -1}
                  onChange={onChangeActiveTestDriveIssuesNoted}
                  name="tech-issues"
                  type="checkbox"
                />
                {' '}
                <Label for={btn.value} check>
                  {btn.label}
                  {' '}
                </Label>
              </FormGroup>
            )}
            </FormGroup>
            : 
            <FormGroup tag="fieldset">
            <legend>Possible partner issues:</legend>
            {noTechBtns.map((btn, index) =>
              <FormGroup check key={index}>
                <Input
                  id={btn.value}
                  value={btn.value}
                  checked={activeTestDriveIssuesNoted.indexOf(btn.value) > -1}
                  onChange={onChangeActiveTestDriveIssuesNoted}
                  name="partner-issues"
                  type="checkbox"
                />
                {' '}
                <Label for={btn.value} check>
                  {btn.label}
                  {' '}
                </Label>
              </FormGroup>
            )}
            </FormGroup>
          }
        </Col>
        <Col sm="6">
          <FormGroup tag="fieldset">
          <legend>Motorically, did you observe:</legend>
          {motorBtns.map((btn, index) =>
            <FormGroup check key={index}>
              <Input
                id={btn.value}
                value={btn.value}
                checked={activeTestDriveIssuesNoted.indexOf(btn.value) > -1}
                onChange={onChangeActiveTestDriveIssuesNoted}
                name="motor-issues"
                type="checkbox"
              />
              {' '}
              <Label for={btn.value} check>
                {btn.label}
                {' '}
              </Label>
            </FormGroup>
          )}
          </FormGroup>
          <FormGroup tag="fieldset">
          <legend>From a cognitive and perceptual perspective, were there challenges with:</legend>
          {cogBtns.map((btn, index) =>
            <FormGroup check key={index}>
              <Input
                id={btn.value}
                value={btn.value}
                checked={activeTestDriveIssuesNoted.indexOf(btn.value) > -1}
                onChange={onChangeActiveTestDriveIssuesNoted}
                name="cog-issues"
                type="checkbox"
              />
              {' '}
              <Label for={btn.value} check>
                {btn.label}
                {' '}
              </Label>
            </FormGroup>
          )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col>
        <FormGroup>
          <Label for="practitioner-comments" className="aa-bold-text">
                Your comments:
          </Label>
          <Input
            id="practitioner-comments"
            value= {activeTestDrivePractitionerComments}
            onChange= {onChangeActiveTestDrivePractitionerComments}
            name="practitioner-comments"
            type="textarea"
          />
        </FormGroup>
        </Col>
      </Row>
    </Form>
    </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function TestDriveRateIssues(props) {
  const firstName = props.firstName;
  const isLowTechPath = props.isLowTechPath;
  const activeAccessMethodCategory = props.activeAccessMethodCategory;
  const activeTestDriveIssuesNoted = props.activeTestDriveIssuesNoted;
  const activeTestDrivePractitionerComments = props.activeTestDrivePractitionerComments;
  const onChangeActiveTestDriveIssuesNoted = props.onChangeActiveTestDriveIssuesNoted;
  const onChangeActiveTestDrivePractitionerComments = props.onChangeActiveTestDrivePractitionerComments;
  const saveFormData = props.saveFormData;
  const onContinue = props.onContinue;
  const dev = props.dev || {};

  const pageData = {
    image: "",
    faIcon: "",
    pageSymbolClassName: "aa-section-symbol test-drive",
    title: `How did that Test-drive go, in more detail?`,
    subTitle: '',
    learnMore: {linkText: "Learn More about Test-drive issues",
                title: "Identifying Test-drive issues",
                content: TD_RATE_ISSUES_HELP
                },
    nextRoute: "",
    sidebar: false,
    rightMargin: false
  }

  console.log("Rendering TestDriveRateIssues");

  useEffect(() => {
    return () => {
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <TestDriveRateIssuesContent 
          firstName={firstName}
          isLowTechPath={isLowTechPath}
          activeAccessMethodCategory={activeAccessMethodCategory}
          activeTestDriveIssuesNoted={activeTestDriveIssuesNoted}
          activeTestDrivePractitionerComments={activeTestDrivePractitionerComments}
          onChangeActiveTestDriveIssuesNoted={onChangeActiveTestDriveIssuesNoted}
          onChangeActiveTestDrivePractitionerComments={onChangeActiveTestDrivePractitionerComments}
        />
      }
      sidebarComponent={
        dev.showDevSidebar 
        &&
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
        />  
      }
      onContinue={onContinue}  
    />
    </>
  );
}

TestDriveRateIssues.propTypes = propTypes;
TestDriveRateIssues.defaultProps = defaultProps;

export default TestDriveRateIssues;